import { Autocomplete, TextField } from '@mui/material';
import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { IDropdownResponse } from '../../models';
import { SearchAPI } from '../../fetch/search';
import { useToastContext } from '../../hooks';
import { useDebounce } from '../../hooks/useDebounce';

interface ISearchAheadDropdown {
  id: string;
  value: any;
  onChange: (newValue: any) => void;
  label: string;
  placeholder: string;
}
export const SearchAheadDropdown: FC<PropsWithChildren<ISearchAheadDropdown>> = ({
  id,
  value,
  onChange,
  label,
  placeholder,
  children,
}) => {
  const { showToast } = useToastContext();
  // Protected Clients
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [companies, setCompanies] = useState<IDropdownResponse[]>([]);
  const initialText = typeof value === 'string' ? value : '';
  const [searchValue, setSearchValue] = useState<string>(initialText);
  const [debouncedValue, setDebouncedValue] = useState<string>(initialText);
  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const companies = await SearchAPI.fetchCompanies(debouncedValue, 100);
      // Merge uniquely by value
      setCompanies(prev => {
        const map = new Map<string | number, IDropdownResponse>();
        [...prev, ...companies].forEach(c => map.set(c.value, c));
        return Array.from(map.values());
      });
    } catch (error: any) {
      showToast('error', error?.detail || 'Error trying to retrieve Companies. Please try again.');
    } finally {
      setLoadingCompanies(false);
    }
  };

  useDebounce(
    () => {
      setDebouncedValue(searchValue);
    },
    500,
    [searchValue]
  );

  useEffect(() => {
    if (debouncedValue) {
      fetchCompanies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  return (
    <Autocomplete
      id={id}
      value={value}
      onChange={(e, newValue) => {
        onChange(newValue);
      }}
      loading={loadingCompanies}
      options={companies}
      inputValue={searchValue ?? ''}
      onInputChange={(event, newInputValue) => {
        setSearchValue(newInputValue);
      }}
      getOptionLabel={option => {
        return option?.text ?? '';
      }}
      renderOption={(props, option: IDropdownResponse) => {
        return (
          <li {...props} key={`${id}-${option.value}`}>
            {option.text}
          </li>
        );
      }}
      renderInput={params => <TextField {...params} label={label} placeholder={placeholder} />}
    />
  );
};
